/* =====================================================
   afk-audio — motion layer
   Scroll reveals with stagger, LED scroll-progress bar,
   and scroll-scrubbed video (Apple-style) for sections
   with [data-scrub]. Everything is progressive
   enhancement: without JS the page renders fully, and
   prefers-reduced-motion disables the lot.
   ===================================================== */
(function () {
  'use strict';
  if (window.__afkEyeCandy) return;
  window.__afkEyeCandy = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- scroll reveals ---------- */
  var REVEAL_SELECTORS = [
    '.how-card', '.usecase-card', '.product-card', '.principle-card',
    '.link-tile', '.team-card', '.pq-card', '.pq-lead', '.spec-tile',
    '.contact-card', '.specgroup', '.compare-card', '.compare__photo',
    '.story__milestone', '.pdemo', '.cfgapp', '.artist-card',
    '.changelog__release', '.bundle__card', '.bundle__item',
    '.object__media', '.story__img', '.demo__frame', '.featstat__punch',
    '.cmp-table tbody tr', '.faq-item'
  ].join(', ');

  var observer = null;

  function initReveals() {
    if (reduced || !('IntersectionObserver' in window)) return;
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    }
    document.querySelectorAll(REVEAL_SELECTORS).forEach(function (el) {
      if ('reveal' in el.dataset) return;
      // skip anything already on screen at boot: no popping on first paint
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
        el.dataset.reveal = 'done';
        return;
      }
      el.dataset.reveal = '';
      var parent = el.parentElement;
      var index = parent ? Array.prototype.indexOf.call(parent.children, el) : 0;
      el.style.transitionDelay = Math.min(index % 9, 5) * 70 + 'ms';
      observer.observe(el);
    });
  }

  /* ---------- scroll-scrubbed video ---------- */
  function initScrub() {
    document.querySelectorAll('video[data-scrub]').forEach(function (video) {
      if (video.dataset.scrubInit) return;
      video.dataset.scrubInit = '1';
      if (reduced) { video.removeAttribute('data-scrub'); return; }
      // Mobile: scroll-driven currentTime seeking stutters badly (esp. iOS Safari) and a
      // wide LED-strip clip reads as a thin flicker on a phone. Play it as a gentle loop instead.
      if (window.matchMedia('(max-width: 768px)').matches) {
        video.removeAttribute('data-scrub');
        video.muted = true; video.loop = true; video.setAttribute('playsinline', '');
        var pm = video.play(); if (pm && pm.catch) pm.catch(function () {});
        return;
      }
      video.pause();
      video.preload = 'auto';
      var target = 0, current = 0, raf = null;

      function progressFor() {
        var rect = video.getBoundingClientRect();
        // 0 when the strip enters from below, 1 when it leaves at the top
        var total = window.innerHeight + rect.height;
        return Math.min(1, Math.max(0, (window.innerHeight - rect.top) / total));
      }
      function tick() {
        current += (target - current) * 0.12; // ease toward target
        if (video.duration) {
          video.currentTime = current * video.duration * 0.98;
        }
        if (Math.abs(target - current) > 0.001) {
          raf = requestAnimationFrame(tick);
        } else {
          raf = null;
        }
      }
      function onScroll() {
        target = progressFor();
        if (!raf) raf = requestAnimationFrame(tick);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      if (video.readyState >= 1) { onScroll(); }
      else { video.addEventListener('loadedmetadata', onScroll, { once: true }); }
    });
  }

  /* ---------- lazy muted loops ---------- */
  // video[data-lazy-play] (snippets/lazy-video.liquid) has no autoplay and preload="none", so
  // nothing downloads until a loop comes within 200 px of the screen. It plays in view and
  // pauses off screen. Reduced motion and Save-Data keep the poster until the visitor starts it.
  // Each loop gets a pause/play control (visible on phones; on desktop only on keyboard focus or
  // while the loop waits on its poster); a click
  // on the loop itself does the same. A refused play() (iOS Low Power Mode) leaves the poster
  // and the control, which then starts it from a real tap.
  // HLS (data-hls) is for WebKit phones, which stream it adaptively: it goes in as the first
  // <source> right before the first play(). Chromium plays HLS too, but starts at 480p and then
  // climbs to 1080p whatever the frame size, so there the 720p mp4 from the markup is lighter and
  // sharper. Nothing is touched before play(), so nothing loads early (preload="none").
  var videoIO = null;
  var still = reduced || !!(navigator.connection && navigator.connection.saveData);
  var webkitHls = /Apple/.test(navigator.vendor || '');
  var phoneMQ = window.matchMedia('(max-width: 749px)');

  // is-held: the loop waits on its poster for the visitor (reduced motion, Save-Data, refused
  // autoplay, paused by hand). Desktop shows the control only then, so a still loop is startable.
  function syncToggle(v) {
    var b = v._afkToggle;
    if (!b) return;
    b.setAttribute('aria-pressed', v.paused ? 'true' : 'false');
    b.classList.toggle('is-held', !!v._afkHeld && v.paused);
  }
  function playLoop(v) {
    var hls = v.getAttribute('data-hls');
    if (hls && webkitHls && phoneMQ.matches) {
      var s = document.createElement('source');
      s.src = hls; s.type = 'application/x-mpegURL';
      v.insertBefore(s, v.firstChild);
      v.load();
    }
    v.removeAttribute('data-hls');
    var p = v.play();
    // AbortError only means pause() (scrolled away) or load() came first: the observer keeps
    // the loop. Any other refusal (NotAllowedError: Low Power Mode) waits for a real tap.
    if (p && p.catch) p.catch(function (e) {
      if (!e || e.name !== 'AbortError') v._afkHeld = true;
      syncToggle(v);
    });
  }
  function flipLoop(v) {
    if (v.paused) { v._afkHeld = false; playLoop(v); }
    else { v._afkHeld = true; v.pause(); }
  }
  function addToggle(v) {
    var host = v.parentElement;
    if (!host || v.controls) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'lv-toggle';
    // name each control after its section (badge, loud word or heading), so a screen reader can
    // tell the loops on one page apart; syncToggle only changes aria-pressed, the name stays
    var sec = host.closest('section, [data-section]');
    var cue = sec && sec.querySelector('.img-badge, .demo__word, h2, h3');
    var ctx = cue ? cue.textContent.replace(/\s+/g, ' ').trim() : '';
    b.setAttribute('aria-label', (v.getAttribute('data-label-pause') || 'pause video') + (ctx ? ': ' + ctx : ''));
    host.classList.add('lv-host');
    host.insertBefore(b, v.nextSibling);
    v._afkToggle = b;
    b.addEventListener('click', function () { flipLoop(v); });
    v.addEventListener('click', function () { flipLoop(v); });
    v.addEventListener('play', function () { syncToggle(v); });
    v.addEventListener('pause', function () { syncToggle(v); });
    syncToggle(v);
  }
  function initLazyVideos() {
    var vids = document.querySelectorAll('video[data-lazy-play]:not([data-lazy-init])');
    if (!vids.length) return;
    if (!videoIO && 'IntersectionObserver' in window) {
      videoIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) { if (!v._afkHeld) playLoop(v); }
          else if (!v.paused) { v.pause(); }
        });
      }, { rootMargin: '200px 0px' });
    }
    Array.prototype.forEach.call(vids, function (v) {
      v.setAttribute('data-lazy-init', '');
      if (still) v._afkHeld = true;
      addToggle(v);
      if (videoIO) videoIO.observe(v);
      else if (!v._afkHeld) playLoop(v);
    });
  }

  function boot() {
    initReveals();
    initScrub();
    initLazyVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function () {
    initReveals();
    initScrub();
    initLazyVideos();
  });
})();
